/**
 * Settings page.
 * - manager: account info only
 * - company_admin: invite manager + list users they invited
 * - dev: IAM (invite anyone + list companies + list users)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { apiFetch } from '../../api/authApi';
import { getAdminCache, setAdminCache, invalidateAdminCache } from '../../api/adminDataCache';
import { getApiBaseUrl } from '../../api/apiBase';
import { fetchStores } from '../../api/dashboardApi';

const API_BASE_URL = getApiBaseUrl();

/**
 * 所属店舗 fix for one user (dev / company_admin, T4). Same rules as the person's own
 * change in 設定: POS name untouched, goals follow, no POS data needed in the new store.
 * The server checks the store against what the *target* may see.
 */
function HomeStoreFix({ u, stores, onSaved }) {
  const [value, setValue] = useState(u.home_store || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/users/${u.uid}/identity`, {
        method: 'PATCH',
        body: JSON.stringify({ home_store: value }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || res.statusText);
      onSaved(u.uid, d.home_store || value);
    } catch (e) {
      setErr(e.message || '変更できませんでした');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="flex flex-wrap items-center gap-1 text-xs">
      <span className="text-gray-600">{u.stylist_name_pos} · 所属</span>
      <select
        id={`home-store-${u.uid}`}
        className="border border-gray-300 rounded px-1 py-0.5"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
      >
        {!stores.includes(value) && value ? <option value={value}>{value}</option> : null}
        {stores.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <button
        type="button"
        disabled={busy || !value || value === u.home_store}
        onClick={save}
        className="px-2 py-0.5 rounded border border-[#33564B] text-[#33564B] disabled:opacity-40"
      >
        {busy ? '変更中…' : '変更'}
      </button>
      {err && <span className="text-red-600">{err}</span>}
    </span>
  );
}

const SettingsPage = ({ iamMode = false }) => {
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('manager');
  const [inviteBusiness, setInviteBusiness] = useState('hair');
  const [inviteCompanyId, setInviteCompanyId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  // IAM data (dev / company_admin)
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);
  const [storeOptions, setStoreOptions] = useState([]);
  const canFixHomeStore = role === 'dev' || role === 'company_admin';

  useEffect(() => {
    if (!canFixHomeStore) return;
    fetchStores()
      .then((d) => setStoreOptions(d.stores || []))
      .catch(() => setStoreOptions([]));
  }, [canFixHomeStore]);

  const onHomeStoreSaved = (uid, homeStore) => {
    setUsers((prev) => prev.map((x) => (x.uid === uid ? { ...x, home_store: homeStore } : x)));
    invalidateAdminCache();
  };

  useEffect(() => {
    if (!user) return;
    user.getIdTokenResult().then((res) => {
      setRole(res.claims?.role || null);
    });
  }, [user]);

  // Same matrix as access_policy.INVITABLE. A manager's invite is always a stylist of their business.
  const INVITABLE = {
    dev: ['company_admin', 'manager', 'beauty_manager', 'stylist'],
    company_admin: ['manager', 'beauty_manager', 'stylist'],
    manager: ['stylist'],
    beauty_manager: ['stylist'],
  };
  const invitableRoles = INVITABLE[role] || [];
  const canInvite = invitableRoles.length > 0;
  const effectiveInviteRole = invitableRoles.includes(inviteRole) ? inviteRole : invitableRoles[0];
  const ROLE_LABEL = { company_admin: '経営者（company_admin）', manager: 'マネージャー（ヘア）', beauty_manager: 'BeautyManager（アイ＆ネイル）', stylist: 'スタイリスト' };
  const needsBusiness = effectiveInviteRole === 'stylist' && (role === 'dev' || role === 'company_admin');

  // Load companies (dev only). Use cache if fresh (from prefetch on Settings hover).
  useEffect(() => {
    if (role !== 'dev') return;
    const cached = getAdminCache();
    if (cached && Array.isArray(cached.companies)) {
      setCompanies(cached.companies);
      setLoadingCompanies(false);
      return;
    }
    setLoadingCompanies(true);
    setCompaniesError(null);
    apiFetch(`${API_BASE_URL}/admin/companies`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}: ${r.statusText}`);
        return r.json();
      })
      .then((d) => {
        const list = d.companies || [];
        setCompanies(list);
        setAdminCache(list, undefined);
      })
      .catch((err) => {
        setCompaniesError(err.message || 'Failed to load companies');
        setCompanies([]);
      })
      .finally(() => setLoadingCompanies(false));
  }, [role]);

  // Load users (dev: all, company_admin: invited only). Use cache if fresh (dev only).
  useEffect(() => {
    if (!canInvite) return;
    const cached = role === 'dev' ? getAdminCache() : null;
    if (cached && Array.isArray(cached.users)) {
      setUsers(cached.users);
      setLoadingUsers(false);
      return;
    }
    setLoadingUsers(true);
    apiFetch(`${API_BASE_URL}/admin/users`)
      .then((r) => r.json())
      .then((d) => {
        const list = d.users || [];
        setUsers(list);
        if (role === 'dev') setAdminCache(undefined, list);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [role, canInvite]);

  const handleDeleteUser = async (targetUid) => {
    if (!window.confirm('このユーザーのアクセスを無効にしますか？')) return;
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/users/${targetUid}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || res.statusText);
      }
      setUsers((prev) => prev.filter((u) => u.uid !== targetUid));
      invalidateAdminCache();
    } catch (err) {
      setError(err.message || '削除に失敗しました');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setInviteLink(null);
    setSubmitting(true);
    try {
      const body = { email: inviteEmail.trim(), role: effectiveInviteRole };
      if (role === 'dev') body.company_id = inviteCompanyId.trim() || undefined;
      if (needsBusiness) body.business = inviteBusiness;
      const res = await apiFetch(`${API_BASE_URL}/admin/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || res.statusText);
      setMessage(data.message || '招待を送信しました');
      if (data.invite_link) setInviteLink(data.invite_link);
      setInviteEmail('');
      invalidateAdminCache();
      // Refresh users list
      if (canInvite) {
        const ur = await apiFetch(`${API_BASE_URL}/admin/users`);
        const ud = await ur.json();
        setUsers(ud.users || []);
      }
    } catch (err) {
      setError(err.message || '招待に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const copyInviteLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => {
      setMessage((m) => (m ? m + ' リンクをコピーしました' : 'リンクをコピーしました'));
    }).catch(() => setError('コピーに失敗しました'));
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] p-6 font-['Noto_Sans_JP',Helvetica,sans-serif]">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link to="/" className="text-[#33564B] hover:underline text-sm">← ホームに戻る</Link>
        </div>
        <h1 className="text-2xl font-semibold text-[#1a1a1a] mb-8">{iamMode ? 'IAM · ユーザー管理' : '設定'}</h1>

        {canInvite && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <h2 className="text-lg font-medium text-[#33564B] mb-4">
              {role === 'dev' ? 'IAM - ユーザー招待' : 'ユーザー招待'}
            </h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">メールアドレス</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="invite@example.com"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              {invitableRoles.length > 1 && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">役割</label>
                  <select
                    value={effectiveInviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    {invitableRoles.map((r) => (
                      <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
                    ))}
                  </select>
                </div>
              )}
              {invitableRoles.length === 1 && (
                <p className="text-sm text-gray-600">招待できる役割: {ROLE_LABEL[invitableRoles[0]]}（あなたの業態の店舗のみ閲覧できます）</p>
              )}
              {needsBusiness && (
                <div>
                  <label className="block text-sm text-gray-700 mb-1">業態</label>
                  <select
                    value={inviteBusiness}
                    onChange={(e) => setInviteBusiness(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="hair">ヘア</option>
                    <option value="beauty">アイ＆ネイル</option>
                  </select>
                </div>
              )}
              {role === 'dev' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">企業</label>
                    <select
                      value={inviteCompanyId}
                      onChange={(e) => setInviteCompanyId(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">選択してください</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name || c.id} ({c.stores?.length || 0}店舗)
                        </option>
                      ))}
                      {loadingCompanies && companies.length === 0 && (
                        <option disabled>読み込み中...</option>
                      )}
                    </select>
                  </div>
                </>
              )}
              {role === 'company_admin' && (
                <p className="text-sm text-gray-500">manager を自社に招待します</p>
              )}
              {message && <p className="text-sm text-green-600">{message}</p>}
              {error && <p className="text-sm text-red-600">{error}</p>}
              {inviteLink && (
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">招待リンク（コピーして転送）</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteLink}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={copyInviteLink}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm shrink-0"
                    >
                      コピー
                    </button>
                  </div>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-[#33564B] text-white rounded-lg hover:bg-[#2a463d] disabled:opacity-50"
              >
                {submitting ? '送信中...' : '招待を送信'}
              </button>
            </form>
          </section>
        )}

        {role === 'dev' && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <h2 className="text-lg font-medium text-[#33564B] mb-4">企業一覧</h2>
            {loadingCompanies ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : companiesError ? (
              <p className="text-sm text-red-600">{companiesError}</p>
            ) : companies.length === 0 ? (
              <p className="text-sm text-gray-500">企業がありません。seed_companies.py で登録してください。</p>
            ) : (
              <ul className="space-y-2">
                {companies.map((c) => (
                  <li key={c.id} className="text-sm flex justify-between">
                    <span className="font-medium">{c.name || c.id}</span>
                    <span className="text-gray-600">({c.stores?.length || 0}店舗)</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {canInvite && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <h2 className="text-lg font-medium text-[#33564B] mb-4">
              {role === 'company_admin' ? '招待したユーザー' : 'ユーザー一覧'}
            </h2>
            {loadingUsers ? (
              <p className="text-sm text-gray-500">読み込み中...</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-gray-500">
                {role === 'company_admin' ? 'まだ招待したユーザーはいません。' : 'ユーザーがありません。'}
              </p>
            ) : (
              <ul className="space-y-2">
                {users.map((u) => (
                  <li key={u.uid} className="text-sm flex flex-wrap justify-between items-center gap-2">
                    <span className="flex-1 truncate">{u.email}</span>
                    <span className="text-gray-600 text-xs shrink-0">{u.role} / {u.company_id || '-'}</span>
                    {canFixHomeStore && u.stylist_name_pos && u.home_store && (
                      <HomeStoreFix u={u} stores={storeOptions} onSaved={onHomeStoreSaved} />
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteUser(u.uid)}
                      className="shrink-0 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200"
                    >
                      削除
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-lg font-medium text-[#33564B] mb-2">アカウント情報</h2>
          <p className="text-sm text-gray-600">{user?.email}</p>
          <p className="text-sm text-gray-500 mt-1">役割: {role || '-'}</p>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
